import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Container,
} from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import DeleteIcon from '@mui/icons-material/Delete'; // Added DeleteIcon

// Math-related icons
import CalculateIcon from '@mui/icons-material/Calculate';
import FunctionsIcon from '@mui/icons-material/Functions';
import NumbersIcon from '@mui/icons-material/Numbers';
import PercentIcon from '@mui/icons-material/Percent';
import PlusOneIcon from '@mui/icons-material/PlusOne';
import ExposureIcon from '@mui/icons-material/Exposure';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import StraightenIcon from '@mui/icons-material/Straighten';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import TableChartIcon from '@mui/icons-material/TableChart';
import OneKIcon from '@mui/icons-material/OneK';
import TwoKIcon from '@mui/icons-material/TwoK';
import ThreeKIcon from '@mui/icons-material/ThreeK';
import FourKIcon from '@mui/icons-material/FourK';
import FiveKIcon from '@mui/icons-material/FiveK';
import SixKIcon from '@mui/icons-material/SixK';
import SevenKIcon from '@mui/icons-material/SevenK';
import EightKIcon from '@mui/icons-material/EightK';
import NineKIcon from '@mui/icons-material/NineK';

// Modern color palette
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(2),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1, 2),
  color: '#e74c3c',
  borderColor: '#e74c3c',
  '&:hover': {
    backgroundColor: '#e74c3c',
    color: 'white',
    borderColor: '#e74c3c',
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 },
  maxWidth: '100%',
  backgroundColor: 'white',
  borderRadius: { xs: '8px', sm: '12px' },
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  padding: { xs: theme.spacing(2), sm: theme.spacing(4) },
  maxHeight: '90vh',
  overflowY: 'auto',
}));

const Subjects = () => {
  const [subjects, setSubjects] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const navigate = useNavigate();

  const iconMap = {
    Calculate: <CalculateIcon />,
    Functions: <FunctionsIcon />,
    Numbers: <NumbersIcon />,
    Percent: <PercentIcon />,
    PlusOne: <PlusOneIcon />,
    Exposure: <ExposureIcon />,
    SquareFoot: <SquareFootIcon />,
    Straighten: <StraightenIcon />,
    Timeline: <TimelineIcon />,
    ShowChart: <ShowChartIcon />,
    BarChart: <BarChartIcon />,
    PieChart: <PieChartIcon />,
    ScatterPlot: <ScatterPlotIcon />,
    TableChart: <TableChartIcon />,
    OneK: <OneKIcon />,
    TwoK: <TwoKIcon />,
    ThreeK: <ThreeKIcon />,
    FourK: <FourKIcon />,
    FiveK: <FiveKIcon />,
    SixK: <SixKIcon />,
    SevenK: <SevenKIcon />,
    EightK: <EightKIcon />,
    NineK: <NineKIcon />,
  };

  const iconOptions = [
    { value: 'Calculate', label: 'Calculate', component: <CalculateIcon /> },
    { value: 'Functions', label: 'Functions', component: <FunctionsIcon /> },
    { value: 'Numbers', label: 'Numbers', component: <NumbersIcon /> },
    { value: 'Percent', label: 'Percent', component: <PercentIcon /> },
    { value: 'PlusOne', label: 'Plus One', component: <PlusOneIcon /> },
    { value: 'Exposure', label: 'Arithmetic', component: <ExposureIcon /> },
    { value: 'SquareFoot', label: 'Geometry', component: <SquareFootIcon /> },
    { value: 'Straighten', label: 'Measure', component: <StraightenIcon /> },
    { value: 'Timeline', label: 'Timeline Graph', component: <TimelineIcon /> },
    { value: 'ShowChart', label: 'Line Chart', component: <ShowChartIcon /> },
    { value: 'BarChart', label: 'Bar Chart', component: <BarChartIcon /> },
    { value: 'PieChart', label: 'Pie Chart', component: <PieChartIcon /> },
    { value: 'ScatterPlot', label: 'Scatter Plot', component: <ScatterPlotIcon /> },
    { value: 'TableChart', label: 'Table Chart', component: <TableChartIcon /> },
    { value: 'OneK', label: '1K', component: <OneKIcon /> },
    { value: 'TwoK', label: '2K', component: <TwoKIcon /> },
    { value: 'ThreeK', label: '3K', component: <ThreeKIcon /> },
    { value: 'FourK', label: '4K', component: <FourKIcon /> },
    { value: 'FiveK', label: '5K', component: <FiveKIcon /> },
    { value: 'SixK', label: '6K', component: <SixKIcon /> },
    { value: 'SevenK', label: '7K', component: <SevenKIcon /> },
    { value: 'EightK', label: '8K', component: <EightKIcon /> },
    { value: 'NineK', label: '9K', component: <NineKIcon /> },
  ];

  const colorOptions = [
    { value: '#ffffff', label: 'White' },
    { value: '#f8d7da', label: 'Light Red' },
    { value: '#d4edda', label: 'Light Green' },
    { value: '#cce5ff', label: 'Light Blue' },
    { value: '#fff3cd', label: 'Light Yellow' },
    { value: '#e2e3e5', label: 'Light Gray' },
    { value: '#f5c6cb', label: 'Soft Pink' },
    { value: '#c3e6cb', label: 'Mint Green' },
    { value: '#b8daff', label: 'Sky Blue' },
    { value: '#ffeeba', label: 'Pale Yellow' },
    { value: '#d6bcfa', label: 'Lavender' },
    { value: '#fdd0e0', label: 'Baby Pink' },
    { value: '#b5f5e2', label: 'Aqua' },
    { value: '#fed7aa', label: 'Peach' },
    { value: '#e9d8fd', label: 'Lilac' },
    { value: '#a3bffa', label: 'Periwinkle' },
    { value: '#fed7d7', label: 'Coral' },
    { value: '#bef264', label: 'Lime' },
    { value: '#bbf7d0', label: 'Pistachio' },
    { value: '#bfdbfe', label: 'Cornflower Blue' },
  ];

  useEffect(() => {
    const fetchSubjects = async () => {
      setIsLoading(true);
      const subjectsCollection = collection(db, 'subjects');
      const subjectsQuery = query(subjectsCollection);

      try {
        const querySnapshot = await getDocs(subjectsQuery);
        const subjectsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubjects(subjectsData);
      } catch (error) {
        console.error('Error fetching subjects:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  const filteredSubjects = subjects.filter((subject) =>
    subject.name.toLowerCase().includes(searchText.toLowerCase()) ||
    subject.description.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleEditClick = (subject) => {
    setSelectedSubject(subject);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSubject(null);
  };

  const handleSaveSubject = async () => {
    if (!selectedSubject) return;

    try {
      const subjectRef = doc(db, 'subjects', selectedSubject.id);
      await updateDoc(subjectRef, {
        name: selectedSubject.name,
        description: selectedSubject.description,
        icon: selectedSubject.icon,
        backgroundColor: selectedSubject.backgroundColor,
      });

      setSubjects(subjects.map((sub) =>
        sub.id === selectedSubject.id ? selectedSubject : sub
      ));
      handleCloseModal();
    } catch (error) {
      console.error('Error updating subject:', error);
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (window.confirm('Are you sure you want to delete this subject? This action cannot be undone.')) {
      try {
        const subjectRef = doc(db, 'subjects', subjectId);
        await deleteDoc(subjectRef);
        setSubjects(subjects.filter((sub) => sub.id !== subjectId));
      } catch (error) {
        console.error('Error deleting subject:', error);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedSubject((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const columns = [
    {
      field: 'icon',
      headerName: 'Icon',
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {iconMap[params.value] || <Typography variant="body2">No Icon</Typography>}
        </Box>
      ),
    },
    { field: 'name', headerName: 'Subject Name', width: 250 },
    { field: 'description', headerName: 'Description', width: 450 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250, // Increased width to accommodate two buttons
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row)}
          >
            Edit
          </AnimatedButton>
          <DeleteButton
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteSubject(params.row.id)}
          >
            Delete
          </DeleteButton>
        </Box>
      ),
    },
  ];

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <SchoolIcon sx={{ fontSize: 35, mr: 1 }} />
            Subjects
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Manage and edit subjects in the portal.
          </Typography>
        </Box>

        {/* Alert */}
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: '10px',
            bgcolor: `${colors.accent}20`,
            color: colors.secondary,
            '& .MuiAlert-icon': { color: colors.accent },
          }}
        >
          This is the Subjects module. Only Admin roles can access this module.
        </Alert>

        {/* Subjects Table */}
        <ModernCard>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: colors.secondary, fontWeight: '600' }}>
                Subject List
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <AnimatedButton
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/addsubjects')}
                >
                  Add Subject
                </AnimatedButton>
                <TextField
                  label="Search Subjects"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search by name or description"
                  sx={{
                    width: { xs: '100%', sm: '300px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                  }}
                />
              </Box>
            </Box>
            <DataGrid
              rows={filteredSubjects}
              columns={columns}
              pageSize={10}
              checkboxSelection={false}
              loading={isLoading}
              sx={{
                '& .MuiDataGrid-root': { border: 'none' },
                '& .MuiDataGrid-cell': { color: colors.secondary },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: colors.background,
                  color: colors.secondary,
                  fontWeight: '600',
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
              }}
            />
          </Box>
        </ModernCard>

        {/* Edit Modal */}
        <Modal open={openModal} onClose={handleCloseModal}>
          <ModalBox>
            <Typography
              variant="h5"
              sx={{ fontWeight: '700', color: colors.secondary, mb: 3 }}
            >
              Edit Subject
            </Typography>
            {selectedSubject && (
              <Box component="form">
                <TextField
                  fullWidth
                  label="Subject Name"
                  name="name"
                  value={selectedSubject.name}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={selectedSubject.description}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel sx={{ color: colors.secondary }}>Icon</InputLabel>
                  <Select
                    name="icon"
                    value={selectedSubject.icon}
                    onChange={handleInputChange}
                    renderValue={(selected) => {
                      const selectedIcon = iconOptions.find((icon) => icon.value === selected);
                      return selectedIcon ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {selectedIcon.component}
                          <Typography sx={{ ml: 1 }}>{selectedIcon.label}</Typography>
                        </Box>
                      ) : '';
                    }}
                    sx={{
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {iconOptions.map((icon) => (
                      <MenuItem key={icon.value} value={icon.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {icon.component}
                          <Typography sx={{ ml: 1 }}>{icon.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: colors.secondary }}>Background Color</InputLabel>
                  <Select
                    name="backgroundColor"
                    value={selectedSubject.backgroundColor}
                    onChange={handleInputChange}
                    sx={{
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                    }}
                  >
                    {colorOptions.map((color) => (
                      <MenuItem key={color.value} value={color.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: color.value,
                              mr: 1,
                              border: '1px solid #ccc',
                            }}
                          />
                          {color.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <AnimatedButton
                    variant="contained"
                    color="primary"
                    onClick={handleSaveSubject}
                  >
                    Save
                  </AnimatedButton>
                  <AnimatedButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </AnimatedButton>
                </Box>
              </Box>
            )}
          </ModalBox>
        </Modal>
      </Container>
    </div>
  );
};

export default Subjects;